import { useEffect, useRef, useState } from "react"
import { Trash2 } from "lucide-react"
import { hsvToHex, hexToHsv, type Hsv } from "@/lib/location-scene-3d/color-convert"
import { WALL_COLOR_PRESET_GREEN } from "@/lib/location-scene-3d/solid-color-face"
import { cn } from "@/lib/utils"

type SceneColorPickerProps = {
  value: string
  onChange: (hex: string) => void
  onPreset?: (hex: string) => void
  onClear?: () => void
  disabled?: boolean
  className?: string
  labels: {
    color: string
    presetGreen?: string
    clearColor?: string
  }
}

const HUE_HEIGHT = 14

function hueGradientCss(): string {
  return "linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)"
}

function svBackground(hue: number): string {
  const pure = hsvToHex({ h: hue, s: 1, v: 1 })
  return `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${pure})`
}

export function SceneColorPicker({
  value,
  onChange,
  onPreset,
  onClear,
  disabled,
  className,
  labels,
}: SceneColorPickerProps) {
  const initial = hexToHsv(value) ?? { h: 0, s: 0, v: 0.78 }
  const [hsv, setHsv] = useState<Hsv>(initial)
  const emitRafRef = useRef<number | null>(null)
  const pendingHexRef = useRef<string | null>(null)
  const svRef = useRef<HTMLDivElement>(null)
  const hueRef = useRef<HTMLDivElement>(null)
  const dragKindRef = useRef<"sv" | "hue" | null>(null)

  useEffect(() => {
    const next = hexToHsv(value)
    if (!next) return
    setHsv(next)
  }, [value])

  useEffect(() => {
    return () => {
      if (emitRafRef.current != null) cancelAnimationFrame(emitRafRef.current)
    }
  }, [])

  const emitHex = (hex: string) => {
    pendingHexRef.current = hex
    if (emitRafRef.current != null) return
    emitRafRef.current = requestAnimationFrame(() => {
      emitRafRef.current = null
      const pending = pendingHexRef.current
      if (pending) onChange(pending)
    })
  }

  const applyHsv = (next: Hsv) => {
    setHsv(next)
    emitHex(hsvToHex(next))
  }

  const readSv = (clientX: number, clientY: number) => {
    const el = svRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const s = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(rect.width, 1)))
    const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / Math.max(rect.height, 1)))
    applyHsv({ h: hsv.h, s, v })
  }

  const readHue = (clientX: number) => {
    const el = hueRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const t = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(rect.width, 1)))
    applyHsv({ h: t * 360, s: hsv.s, v: hsv.v })
  }

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (!dragKindRef.current || disabled) return
      if (dragKindRef.current === "sv") readSv(event.clientX, event.clientY)
      else readHue(event.clientX)
    }
    const onUp = () => {
      dragKindRef.current = null
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
    window.addEventListener("pointercancel", onUp)
    return () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      window.removeEventListener("pointercancel", onUp)
    }
    // hsv captured for readSv/readHue — intentional for drag session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, hsv.h, hsv.s, hsv.v])

  const hex = hsvToHex(hsv)

  return (
    <div
      className={cn(
        "flex w-[13.5rem] flex-col gap-2 rounded-lg border border-ui-border bg-ui-surface p-2 shadow-md",
        className,
      )}
      role="group"
      aria-label={labels.color}
    >
      <div
        ref={svRef}
        className="relative aspect-square w-full cursor-crosshair rounded-md border border-ui-border"
        style={{ background: svBackground(hsv.h) }}
        onPointerDown={(event) => {
          if (disabled) return
          event.preventDefault()
          dragKindRef.current = "sv"
          ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
          readSv(event.clientX, event.clientY)
        }}
      >
        <span
          className="pointer-events-none absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: hex }}
        />
      </div>

      <div className="flex items-center gap-2">
        <div
          className="h-7 w-7 shrink-0 rounded-full border border-ui-border"
          style={{ backgroundColor: hex }}
          aria-hidden
        />
        <div
          ref={hueRef}
          className="relative h-3.5 flex-1 cursor-pointer rounded-full border border-ui-border"
          style={{ height: HUE_HEIGHT, background: hueGradientCss() }}
          onPointerDown={(event) => {
            if (disabled) return
            event.preventDefault()
            dragKindRef.current = "hue"
            ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
            readHue(event.clientX)
          }}
        >
          <span
            className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
            style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }}
          />
        </div>
      </div>

      {onPreset || onClear ? (
        <div className="flex items-center gap-1.5">
          {onPreset ? (
            <button
              type="button"
              disabled={disabled}
              title={labels.presetGreen}
              aria-label={labels.presetGreen}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-ui-border leading-none disabled:cursor-not-allowed disabled:opacity-50"
              style={{ backgroundColor: WALL_COLOR_PRESET_GREEN }}
              onClick={() => onPreset(WALL_COLOR_PRESET_GREEN)}
            />
          ) : null}
          {onClear ? (
            <button
              type="button"
              disabled={disabled}
              title={labels.clearColor}
              aria-label={labels.clearColor}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-ui-border bg-ui-surface-sunken leading-none text-ui-text-secondary hover:bg-red-600/80 hover:text-ui-on-accent disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => onClear()}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
